<template>
  <div class="asc-power-bi-airline-workspace-grid">
    <asc-ag-grid-filter
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import { AscAgGridFilter, AscDrillDownCellRenderer } from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'
import { dateStringMatcher } from '@/components/agGrid/AgGridHelperMethods'
import { getAscFormattedDateTime } from '@/utilities/dates'
import AscAgGridEllipseRenderer from '@/components/agGrid/AscAgGridEllipseRenderer'
import AscConfirmation from '@/components/molecules/AscConfirmation'

export default {
  name: 'AscPowerBiAirlineWorkspaceGrid',

  components: {
    AscAgGridFilter,
    AscAgGridEllipseRenderer,
    AscDrillDownCellRenderer,
    AscConfirmation,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],
  props: {
    selectedWorkspace: {
      type: Object,
      default: null,
    },
    updateSelectedDashboard: {
      type: Function,
      default: () => {},
    },
    isCollinsWorkspaceTab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          floatingFilter: false,
        },
        localeText: { noRowsToShow: this.getMappedValuesOrDefaultText() },
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
      }),
      isCloseClicked: true,
      duplicateConfirmationToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Dashboard Duplicated',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
          hasToastAction: false,
          icon: 'mdi-check',
          duration: 6000,
        },
      ],
    }
  },

  computed: {
    mappedValues() {
      return this.values
        .filter(
          item =>
            item.pbiDatasets[0]?.pbiWorkspaceId === this.selectedWorkspaceId,
        )
        .map(item => {
          return {
            ...item,
            dataset: item.pbiDatasets[0].name,
            sharedWith: item.isShared
              ? `All ${item.pbiDatasets[0].pbiWorkspaceName} Users`
              : 'Only Me',
          }
        })
    },
    agGridRowData() {
      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: '',
          field: 'id',
          cellRenderer: function () {
            return '<span><i class="mdi mdi-chart-areaspline chart-icon"></i></span>'
          },
          width: 44,
          minWidth: 44,
          maxWidth: 44,
          sortable: false,
        },
        {
          headerName: 'Name',
          field: 'name',
          wrapText: true,
          autoHeight: true,
          cellStyle: { 'word-break': 'normal' },
          sort: 'asc',
          cellClass: ['ag-cell--drill', 'ag-cell--drill-ripple'],
          cellRenderer: 'AscDrillDownCellRenderer',
        },
        {
          headerName: 'Dataset',
          field: 'dataset',
        },
        {
          headerName: 'Last Edited',
          field: 'lastEdited',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          valueFormatter: data => getAscFormattedDateTime(data.value),
        },
        {
          headerName: 'Creator',
          field: 'creator',
          hide: this.isCollinsWorkspaceTab,
        },
        {
          headerName: 'Shared With',
          field: 'sharedWith',
          hide: this.isCollinsWorkspaceTab,
        },
        {
          headerName: '',
          field: '',
          cellRenderer: 'AscAgGridEllipseRenderer',
          cellRendererParams: {
            iconOnly: 'mdi-dots-vertical',
            callback: arg => this.handleEllipsesClick(arg),
          },
          cellStyle: { 'padding-top': 0 },
          minWidth: 44,
          maxWidth: 44,
          width: 44,
        },
      ]
    },
    selectedWorkspaceId() {
      return this.selectedWorkspace.id
    },
  },

  methods: {
    getMappedValuesOrDefaultText() {
      return this.mappedValues == null
        ? 'There are no dashboards saved in ' +
            this.selectedWorkspace.friendlyName
        : this.mappedValues
    },
    handleCellClicked(event) {
      if (event) {
        this.updateSelectedDashboard(event.data)
      }
    },
    handleEllipsesClick(selectedItem) {
      if (selectedItem.selectionKey === 'Manage Permissions') {
        this.$emit('updatePermissions', selectedItem)
      } else if (
        selectedItem.selectionKey === 'Duplicate' ||
        selectedItem.selectionKey === 'Duplicate to My Airline Workspace'
      ) {
        this.$emit('handleDuplicateReport', selectedItem)
      } else if (selectedItem.selectionKey === 'Delete') {
        this.$emit('deleteReport', selectedItem)
      } else if (selectedItem.selectionKey === 'View') {
        this.updateSelectedDashboard(selectedItem.selectedObject)
      } else if (selectedItem.selectionKey === 'Edit') {
        this.updateSelectedDashboard(selectedItem.selectedObject)
        this.$emit('handleSetReadonlyMode', false)
      } else {
        console.log(selectedItem)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-power-bi-airline-workspace-grid {
  .chart-icon {
    color: $accent-600;
  }
  .ag-theme-balham {
    .ag-row {
      &.ag-row-selected {
        background-color: $accent-100;
      }

      &.ag-row-hover {
        cursor: pointer;

        &:not(.ag-row-selected) {
          background-color: $accent-0;
        }
      }
    }
  }
}
</style>
